/**
 * Menu Item Components
 * Contains: MenuItemSizeSelector, MenuItemRow, MenuItemCard
 * NOTE: Behavior copied from legacy App.js
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Plus, Minus, ShoppingCart } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function MenuItemSizeSelector({ menuItemId, currentSize, onSizeChange }) {
  const [availableSizes, setAvailableSizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCustomItem, setIsCustomItem] = useState(false);
  
  useEffect(() => {
    const fetchSizes = async () => {
      // Check if this is a custom item (custom items start with "custom-")
      if (menuItemId && menuItemId.toString().startsWith('custom-')) {
        setIsCustomItem(true);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await axios.get(`${API}/api/menu-items/${menuItemId}`);
        const menuItem = response.data;
        
        if (menuItem && menuItem.pricing) {
          const sizes = Object.keys(menuItem.pricing).filter(size => menuItem.pricing[size] > 0);
          setAvailableSizes(sizes);
        }
      } catch (error) {
        console.error('Failed to fetch menu item sizes:', error);
        // If fetch fails, we'll show the current size as a badge
      } finally {
        setLoading(false);
      }
    };
    
    fetchSizes();
  }, [menuItemId]);
  
  if (loading) {
    return <div className="text-xs text-gray-400">Loading...</div>;
  }
  
  // For custom items, always show as badge (they have custom pricing)
  if (isCustomItem) {
    return (
      <Badge variant="outline" className="border-orange-300 text-orange-700 text-xs">
        {currentSize ? currentSize.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Custom'}
      </Badge>
    );
  }
  
  // If fetch failed or only one size, show as badge
  if (availableSizes.length <= 1) {
    return (
      <Badge variant="outline" className="border-orange-300 text-orange-700 text-xs">
        {currentSize ? currentSize.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
      </Badge>
    );
  }
  
  // Multiple sizes available - show dropdown
  return (
    <Select value={currentSize} onValueChange={onSizeChange}>
      <SelectTrigger className="border-orange-300 h-7 text-xs w-36">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {availableSizes.map((size) => (
          <SelectItem key={size} value={size} className="text-xs">
            {size.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

// Menu Item Card Component
// Compact Table Row Component for Menu Items

function MenuItemRow({ item, onAddToCart, recommendations, addingToCart, selectedDayMeal, eventDays }) {
  const [selectedTraySize, setSelectedTraySize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [justAdded, setJustAdded] = useState(false);
  const [readyToAdd, setReadyToAdd] = useState(false); // Track when user has selected size manually

  // Determine if item is Piece-based or Tray-based
  const isPieceBased = item.unit_of_measure?.toLowerCase() === 'piece';
  const isTrayBased = item.unit_of_measure?.toLowerCase() === 'tray' || !isPieceBased;
  
  // Get available sizes based on UOM
  const availableSizes = Object.keys(item.pricing || {}).filter(size => item.pricing[size] > 0);
  const suggestedSize = recommendations.suggested || availableSizes[0];
  const isAdding = addingToCart === item.id;

  useEffect(() => {
    if (isPieceBased) {
      // For piece-based, automatically select 'per_piece' if available
      const pieceOption = availableSizes.find(s => s === 'per_piece' || s === 'piece');
      if (pieceOption) {
        setSelectedTraySize(pieceOption);
      }
    } else if (availableSizes.length > 0 && !selectedTraySize) {
      // For tray-based, use suggested or first available
      setSelectedTraySize(suggestedSize || availableSizes[0]);
    }
  }, [availableSizes, suggestedSize, selectedTraySize, isPieceBased]);

  // When user manually selects size or changes quantity, show "ready to add" state
  const handleSizeChange = (newSize) => {
    setSelectedTraySize(newSize);
    setJustAdded(false);
    setReadyToAdd(true); // User selected size - highlight Add button
  };

  const handleQuantityChange = (newQuantity) => {
    setQuantity(newQuantity);
    setJustAdded(false);
    setReadyToAdd(true); // User changed quantity - highlight Add button
  };

  const handleAddToCart = async () => {
    if (selectedTraySize && quantity > 0) {
      await onAddToCart(item, selectedTraySize, quantity, selectedDayMeal?.day, selectedDayMeal?.meal);
      setJustAdded(true); // Mark as just added - turns green
      setReadyToAdd(false); // No longer in "ready to add" state
      setQuantity(1);
    }
  };

  return (
    <div className="border-b border-orange-100 hover:bg-orange-50 transition-colors px-3 py-2 grid grid-cols-12 gap-3 items-center text-sm">
      {/* Item Name - 3 cols */}
      <div className="col-span-3">
        <div className="font-medium text-gray-800 truncate" title={item.name}>{item.name}</div>
        {item.is_vegetarian && (
          <Badge variant="outline" className="text-xs border-green-400 text-green-600 mt-1">Veg</Badge>
        )}
        {item.is_vegan && (
          <Badge variant="outline" className="text-xs border-green-600 text-green-700 mt-1 ml-1">Vegan</Badge>
        )}
      </div>
      
      {/* Type/Info - 2 cols */}
      <div className="col-span-2">
        <div className="text-xs text-gray-600 truncate" title={item.protein_type}>{item.protein_type}</div>
        <div className="text-xs text-orange-600 font-medium">
          {item.unit_of_measure || 'Tray'}
        </div>
        {item.notes && (
          <div className="text-xs text-gray-400 truncate" title={item.notes}>{item.notes}</div>
        )}
      </div>
      
      {/* Size/UOM Selection - 2 cols */}
      <div className="col-span-2 pr-2">
        {isPieceBased ? (
          // For piece-based items, show static badge
          <div className="flex items-center gap-1">
            <Badge variant="outline" className="border-orange-300 text-orange-700 text-xs">
              Per Piece
            </Badge>
          </div>
        ) : (
          // For tray-based items, show dropdown
          <Select value={selectedTraySize} onValueChange={handleSizeChange}>
            <SelectTrigger className="border-orange-200 h-8 text-xs">
              <SelectValue placeholder="Select size" />
            </SelectTrigger>
            <SelectContent>
              {availableSizes.map((size) => (
                <SelectItem key={size} value={size} className="text-xs">
                  {size.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  {size === suggestedSize && ' ⭐'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>
      
      {/* Quantity Controls - 1 col */}
      <div className="col-span-1 flex items-center justify-center gap-1 px-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuantityChange(Math.max(1, quantity - 1))}
          className="h-6 w-6 p-0 border-orange-300"
        >
          <Minus className="w-3 h-3" />
        </Button>
        <Input
          type="number"
          value={quantity}
          onChange={(e) => handleQuantityChange(Math.max(1, parseInt(e.target.value) || 1))}
          onFocus={(e) => e.target.select()}
          className="w-10 h-6 text-center text-xs border-orange-200 px-1"
          min="1"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => handleQuantityChange(quantity + 1)}
          className="h-6 w-6 p-0 border-orange-300"
        >
          <Plus className="w-3 h-3" />
        </Button>
      </div>
      
      {/* Price - 2 cols - Show only total */}
      <div className="col-span-2 text-right pr-2">
        {selectedTraySize ? (
          <div className="font-semibold text-orange-700">
            ${Math.floor(item.pricing[selectedTraySize] * quantity)}
          </div>
        ) : (
          <div className="text-xs text-gray-400">Select size</div>
        )}
      </div>
      
      {/* Add to Cart Button - 2 cols */}
      <div className="col-span-2 text-center">
        <Button
          onClick={handleAddToCart}
          disabled={!selectedTraySize || isAdding}
          className={`h-8 text-xs px-3 w-full transition-all duration-200 ${
            justAdded 
              ? 'bg-green-600 hover:bg-green-700 text-white' 
              : readyToAdd
                ? 'bg-blue-600 hover:bg-blue-700 text-white animate-pulse ring-2 ring-blue-300 ring-offset-1'
                : 'bg-orange-600 hover:bg-orange-700 text-white'
          }`}
          size="sm"
        >
          {isAdding ? (
            <>
              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
              Adding...
            </>
          ) : justAdded ? (
            <>
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Added!
            </>
          ) : readyToAdd ? (
            <>
              <ShoppingCart className="w-3 h-3 mr-1" />
              Add Now!
            </>
          ) : (
            <>
              <ShoppingCart className="w-3 h-3 mr-1" />
              Add
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

// Keep MenuItemCard for backward compatibility (if used elsewhere)

function MenuItemCard({ item, onAddToCart, recommendations, addingToCart, selectedDayMeal, eventDays }) {
  return (
    <MenuItemRow 
      item={item} 
      onAddToCart={onAddToCart} 
      recommendations={recommendations} 
      addingToCart={addingToCart} 
      selectedDayMeal={selectedDayMeal} 
      eventDays={eventDays} 
    />
  );
}

// Welcome Hint Modal for First-Time Users

export { MenuItemSizeSelector, MenuItemRow, MenuItemCard };
